import { type Link } from "../../../../components/ExpandableItems/types";
import { type FileLike, type PackName } from "../../../../interfaces";
import { type FlowBase } from "../../../../interfaces/flowInfo";
import { type RequestState } from "../../../Application/globaltypes/fetchRequest";

export interface PdfOverviewBaseDto {
  id: number;
  title: string;
  isDraft: boolean;
  ownerId: number;
  publisher: string;
  isPurchased: boolean;
  hasBeenPublished: boolean;
  canBePublished: boolean;
  canBeDeleted: boolean;
  dateCreated: string;
  dateModified: string;
  isLoading?: boolean;
  dependencies?: {
    assignmentsCount: number;
    flowsCount: number;
    packsCount: number;
  };
}

export interface PdfOverviewDto extends PdfOverviewBaseDto {
  thumbnailUrl: string;
}

export interface PdfOverviewDtoV4 extends PdfOverviewBaseDto {
  bag: {
    thumbnailUrl: string;
  };
}

export interface PdfOverview {
  id: number;
  title: string;
  thumbnailUrl: string;
  canBePublished: boolean;
  hasBeenPublished: boolean;
  description: string;
  isDraft: boolean;
  isPurchased: boolean;
  ownerId: number;
  publisher: string;
  dateCreated: string;
  dateModified: string;
  isLoading?: boolean;
  canBeDeleted: boolean;
  dependencies?: {
    assignmentsCount: number;
    flowsCount: number;
    packsCount: number;
  };
  visibility: boolean;
}

export interface PdfDetailsState {
  pdf: PdfView;
  error?: Error;
  isLoading: boolean;
  isLoaded: boolean;
  isUploading: boolean;
  isUploaded: boolean;
  uploadedPdfs: FileLike[];
}

export interface PdfView {
  id: number;
  title: string;
  description: string;
  hasBeenPublished: boolean;
  fileName: string;
  fileUrl: string;
  thumbnailUrl: string;
  isDraft: boolean;
  isPurchased: boolean;
  dateModified?: string;
  dependencies?: {
    assignmentsCount: number;
    flowsCount: number;
    packsCount: number;
  };
  publisherId: number;
  settings: SettingsView;
  visibility: boolean;
}

export interface Pdf {
  id: number;
  title: string;
  description: string;
  hasBeenPublished: boolean;
  fileName: string;
  fileUrl: string;
  thumbnailUrl?: string;
  isDraft: boolean;
  isPurchased: boolean;
  dateModified?: string;
  labels: string[];
  softwareApplications: string[];
  packs: PackName[];
  flows: FlowBase[];
  visibility: boolean;
}

export interface SettingsView {
  packs: Link[];
  flows: Link[];
  labels: string[];
  softwareApplications: string[];
}

export interface PdfTags {
  labels: string[];
  softwareApplications: string[];
}

export interface PublishedPdfOverview {
  id: number;
  title: string;
  thumbnailUrl?: string;
  publisherId: number;
  publisher: string;
}

export interface PdfPerformanceState {
  engagementLineData: RequestState<EngagementLineData[]>;
  engagementHorizontalData: RequestState<EngagementHorizontalData>;
  engagementPeopleData: RequestState<EngagementPeopleData>;
}

export type EngagementLineData = {
  Date: string;
  CompletedCount: number;
  CumulativeCompletedCount: number;
};

export type EngagementHorizontalData = {
  CompletedCount: number;
  Customers: number;
  PDFs: number;
};

export enum EngagementStatus {
  Completed = "Completed",
  NotStarted = "Not Started",
}

export interface EngagementPeople {
  DateWatched: string;
  Status: EngagementStatus;
  UserDepartment: string;
  UserEmail: string;
  UserFirstName: string;
  UserId: number;
  UserLastName: string;
  UserTitle: string;
}

export type EngagementPeopleData = EngagementPeople[];
